import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useApp } from "../../../context/AppState";
import { resolvePreviewIsMobile } from "../../../helper";
import { POPUP_SECTIONS } from "../../../constant/constant";
import { getStepSectionsForPopup } from "../../../utils/popup-step-sections";
import {
  ackDeselectToParent,
  ackSelectBlockToParent,
  isDeselectBlockMessage,
  parseSelectBlockMessage,
} from "../../../utils/preview-selection-messages";
import { isSectionVisible } from "../../../../shared/section-visibility.js";
import PostPurchaseCountdownBanner from "./PostPurchaseCountdownBanner";
import TextBlock from "../../../components/Preview/Draggable/Block/TextBlock";
import PostPurchaseCtaButton from "./PostPurchaseCtaButton";
import ExtensionPreviewSlot from "../ExtensionPreviewSlot";
import { indexPostPurchaseUpsellSections } from "../mapPostPurchaseUpsellSchema";
import PostPurchaseProductSection from "./PostPurchaseProductSection";
import PostPurchaseOrderSummary from "./PostPurchaseOrderSummary";
import AiBrandAttribution from "../../ai-recommendation-upsell/components/AiBrandAttribution";
import styles from "./PostPurchaseUpsellPopup.module.css";

function normalizeStoreLabel(shop) {
  const raw = shop?.shop || shop?.name || "Your store";
  return String(raw)
    .replace(".myshopify.com", "")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

const PostPurchaseUpsellPopup = ({
  popupData,
  popupState,
  setPopupState,
  chrome = "full",
}) => {
  const {
    appState,
    isMobile: viewportIsMobile,
    previewDeviceIsMobile,
    popupGlobalData,
  } = useApp();

  const isMobile = resolvePreviewIsMobile(
    appState,
    viewportIsMobile,
    previewDeviceIsMobile
  );
  const isWidgetChrome = chrome === "widget";

  const popupType = popupData?.popUpType;
  const popupId = popupData?._id;
  const popupRef = useRef(null);

  const activeStep = useMemo(() => {
    const fromPopupState = popupState?.activeStep;
    if (typeof fromPopupState === "number" && !Number.isNaN(fromPopupState)) {
      return fromPopupState;
    }
    if (
      appState?.is_preview_mode &&
      typeof appState?.activeStep === "number" &&
      !Number.isNaN(appState.activeStep)
    ) {
      return appState.activeStep;
    }
    return 0;
  }, [popupState?.activeStep, appState?.is_preview_mode, appState?.activeStep]);

  const stepSections = useMemo(
    () => getStepSectionsForPopup(popupData, activeStep),
    [popupData, activeStep]
  );

  const mapped = useMemo(
    () => indexPostPurchaseUpsellSections(stepSections),
    [stepSections]
  );

  const currentParentData = useMemo(
    () => popupGlobalData?.[`${popupId}_${popupType}`] ?? {},
    [popupGlobalData, popupId, popupType]
  );

  const [selectedBlock, setSelectedBlock] = useState({});
  const selectedBlockRef = useRef(selectedBlock);
  selectedBlockRef.current = selectedBlock;

  const storeLabel = useMemo(
    () => normalizeStoreLabel(appState?.shop),
    [appState?.shop]
  );

  const headlineText = useMemo(() => {
    const section = mapped.headline?.section;
    if (!section) {
      return "It's not too late to add this item to your order!";
    }
    const text =
      section?.text ||
      section?.setting?.text ||
      section?.setting?.display?.text ||
      "";
    return String(text).trim() || "It's not too late to add this item to your order!";
  }, [mapped.headline]);

  const handleBlockClick = useCallback(
    (entry) => {
      if (!appState?.is_preview_mode || !entry) return;

      const { section, index, subIndex, scope } = entry;
      const type = section?.type;
      const prev = selectedBlockRef.current;

      if (scope === "product" && typeof subIndex === "number") {
        if (prev.type === type && prev.index === subIndex && prev.parentIndex === index) {
          return;
        }
        ackSelectBlockToParent({
          type,
          index,
          subIndex,
        });
        setSelectedBlock({ type, index: subIndex, parentIndex: index });
        return;
      }

      if (prev.type === type && prev.index === index) return;
      ackSelectBlockToParent({ type, index });
      setSelectedBlock({ type, index });
    },
    [appState?.is_preview_mode]
  );

  const handleOutSideBlockClick = useCallback(() => {
    setSelectedBlock({});
    if (appState?.is_preview_mode) {
      ackDeselectToParent();
    }
  }, [appState?.is_preview_mode]);

  useEffect(() => {
    if (!appState?.is_preview_mode) return undefined;

    const handleOutsideClick = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        handleOutSideBlockClick();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [appState?.is_preview_mode, handleOutSideBlockClick]);

  useEffect(() => {
    const onMessage = (event) => {
      const data = event?.data;
      if (!data || typeof data !== "object") return;

      if (isDeselectBlockMessage(data)) {
        setSelectedBlock({});
        return;
      }

      const selection = parseSelectBlockMessage(data);
      if (!selection) return;

      const { type, index, subIndex } = selection;
      if (typeof subIndex === "number" && subIndex >= 0) {
        setSelectedBlock({ type, index: subIndex, parentIndex: index });
        return;
      }
      setSelectedBlock({ type, index });
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const handleClose = useCallback(() => {
    setPopupState((prev) => ({
      ...prev,
      [`${popupType}_${popupId}`]: {
        ...prev?.[`${popupType}_${popupId}`],
        openPopup: false,
      },
    }));
  }, [popupId, popupType, setPopupState]);

  const isSelected = (entry) => {
    if (!entry) return false;
    if (entry.scope === "product" && typeof entry.subIndex === "number") {
      return (
        selectedBlock.type === entry.section?.type &&
        selectedBlock.index === entry.subIndex &&
        selectedBlock.parentIndex === entry.index
      );
    }
    return (
      selectedBlock.type === entry.section?.type &&
      selectedBlock.index === entry.index
    );
  };

  const renderCta = (entry, isPrimary = false) => {
    if (!entry || !isSectionVisible(entry.section)) return null;

    const productData = entry.parentSection?.products?.[0];

    return (
      <ExtensionPreviewSlot
        isSelected={isSelected(entry)}
        onClick={() => handleBlockClick(entry)}
      >
        <PostPurchaseCtaButton
          primary={isPrimary}
          popupData={popupData}
          popupState={popupState}
          setPopupState={setPopupState}
          section={entry.section}
          handleClose={handleClose}
          index={entry.scope === "product" ? entry.subIndex : entry.index}
          productIndex={entry.scope === "product" ? 0 : undefined}
          productData={entry.scope === "product" ? productData : undefined}
          parentData={currentParentData}
        />
      </ExtensionPreviewSlot>
    );
  };

  return (
    <div
      ref={popupRef}
      className={`${styles.shell} ${isMobile ? styles.mobile : ""} ${
        isWidgetChrome ? styles.widgetChrome : ""
      }`.trim()}
      data-testid="post-purchase-upsell-preview"
      data-preview-chrome={isWidgetChrome ? "widget" : "full"}
    >
      {!isWidgetChrome ? (
        <header className={styles.header}>
          <h1 className={styles.storeName}>{storeLabel}</h1>
          <div className={styles.statusRow}>
            <span className={styles.statusIcon} aria-hidden>
              ✓
            </span>
            <p className={styles.confirmation}>Confirmation #XXXXXYYYY</p>
          </div>
          <p className={styles.paidNote}>You&apos;ve paid for your order.</p>
          <button type="button" className={styles.viewOrderLink}>
            View order confirmation &gt;
          </button>
        </header>
      ) : null}

      {mapped.headline && isSectionVisible(mapped.headline.section) ? (
        <ExtensionPreviewSlot
          className={styles.headline}
          isSelected={isSelected(mapped.headline)}
          onClick={() => handleBlockClick(mapped.headline)}
        >
          <TextBlock
            section={mapped.headline.section}
            parentData={currentParentData}
          />
        </ExtensionPreviewSlot>
      ) : !mapped.headline ? (
        <div className={styles.headline}>{headlineText}</div>
      ) : null}

      {mapped.countdown && isSectionVisible(mapped.countdown.section) ? (
        <ExtensionPreviewSlot
          className={styles.countdownBanner}
          isSelected={isSelected(mapped.countdown)}
          onClick={() => handleBlockClick(mapped.countdown)}
        >
          <PostPurchaseCountdownBanner
            popupData={popupData}
            section={mapped.countdown.section}
          />
        </ExtensionPreviewSlot>
      ) : null}

      <div className={styles.offerBody}>
        {mapped.product && isSectionVisible(mapped.product.section) ? (
          <ExtensionPreviewSlot
            isSelected={
              selectedBlock.type === POPUP_SECTIONS.PRODUCT &&
              selectedBlock.index === mapped.product.index &&
              selectedBlock.parentIndex == null
            }
            onClick={() => handleBlockClick(mapped.product)}
          >
            <PostPurchaseProductSection
              popupData={popupData}
              section={mapped.product.section}
              activeStep={activeStep}
              index={mapped.product.index}
              footerContent={(previewProduct) => (
                <>
                  <PostPurchaseOrderSummary
                    productSection={mapped.product.section}
                    product={previewProduct}
                    shop={appState?.shop}
                  />
                  <div className={styles.ctaStack}>
                    {renderCta(mapped.primaryCta, true)}
                    {renderCta(mapped.secondaryCta, false)}
                  </div>
                </>
              )}
            />
          </ExtensionPreviewSlot>
        ) : null}
      </div>

      {mapped.brandAttribution && isSectionVisible(mapped.brandAttribution.section) ? (
        <ExtensionPreviewSlot
          className={styles.branding}
          isSelected={isSelected(mapped.brandAttribution)}
          onClick={() => handleBlockClick(mapped.brandAttribution)}
        >
          <AiBrandAttribution section={mapped.brandAttribution.section} />
        </ExtensionPreviewSlot>
      ) : null}

      {!isWidgetChrome ? (
        <footer className={styles.footer}>
          <button type="button" className={styles.footerLink}>
            Privacy policy
          </button>
          <button type="button" className={styles.footerLink}>
            Cancellation policy
          </button>
        </footer>
      ) : null}
    </div>
  );
};

export default PostPurchaseUpsellPopup;
